import { Box, Flex, Heading, HStack, Icon, keyframes, Text, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { useLoaderData } from "react-router-dom";
import { FiList, FiLock, FiEye, FiCalendar, FiMail, FiCheckSquare, FiAward } from "react-icons/fi";
import { AdaptiveShell } from "../OrganizerDashboardView/components/AdaptiveShell";
import { LandingPageDataType, LandingPageDraftDataType, LP_STATUSES, convertHtmlToText, withModel } from "@lubbezposrednio-frontend/core";

import { LandingPageViewModel } from "./LandingPageViewModel";
import { OrganizerData } from "./components/OrganizerData";
import { LandingPageQR } from "./components/LandingPageQR";
import { SignUpForVotingForm } from "./components/SignUpForVotingForm";
import { LandingPageDescriptions } from "./components/LandingPageDescriptions";
import { VotingPreviewBox } from "./components/VotingPreviewBox";

interface LandingPageViewProps {
  model: LandingPageViewModel;
}

const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(0, -22px) scale(1.06); }
`;

const HeroChip: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon, children }) => (
  <HStack position="relative" spacing="6px" bg="rgba(255,255,255,0.14)" border="1px solid rgba(255,255,255,0.2)" borderRadius="full" px="12px" py="6px">
    <Icon as={icon} boxSize="13px" />
    <Text fontSize="13px" fontWeight="600">
      {children}
    </Text>
  </HStack>
);

const formatDay = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("pl-PL", { day: "numeric", month: "long" });
};

const plQuestions = (n: number) =>
  n === 1 ? "pytanie" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "pytania" : "pytań";

const signupLabel = (status: string, startDate: string, endDate: string) => {
  if (status === LP_STATUSES.FINISHED) return "Zapisy zakończone";
  if (status === LP_STATUSES.PLANNED && startDate) return `Zapisy od ${formatDay(startDate)}`;
  return endDate ? `Zapisy do ${formatDay(endDate)}` : "";
};

const STEPS = [
  { icon: FiMail, text: "Zapisujesz się e-mailem (lub jednym kliknięciem, gdy jesteś zalogowany)." },
  { icon: FiCheckSquare, text: "Potwierdzasz adres linkiem z wiadomości." },
  { icon: FiList, text: "Głosujesz anonimowo, gdy głosowanie ruszy." },
  { icon: FiAward, text: "Wynik zostaje zakotwiczony na blockchainie — publicznie weryfikowalny." }
];

const LandingPageView: React.FC<LandingPageViewProps> = observer(({ model }) => {
  const landingPageData = useLoaderData() as
    | (LandingPageDataType | LandingPageDraftDataType)["landing_page"]
    | undefined;

  useEffect(() => {
    document.title = "Strona zapisu na głosowanie";
    landingPageData && model.getInitialData(landingPageData);
  }, []);

  const pageTitle = convertHtmlToText(model.textContents.pageTitle ?? "");
  const pageDescription = convertHtmlToText(model.textContents.pageDescription ?? "");
  const qCount = model.votingPreview?.questions.length ?? 0;
  const signup = signupLabel(model.signupStatus, model.startDate, model.endDate);

  return (
    <AdaptiveShell>
      <Box w="100%" maxW="620px" mx="auto" px={{ base: "16px", md: "20px" }} py={{ base: "28px", md: "44px" }}>
        <VStack align="stretch" spacing="18px">
          {/* HERO — głosowanie */}
            <Box
              position="relative"
              overflow="hidden"
              borderRadius="28px"
              color="white"
              p={{ base: "30px 26px", md: "44px 42px" }}
              bgGradient="linear(150deg, #25004f 0%, #430098 46%, #6a1fc2 100%)"
              boxShadow="0 24px 60px rgba(40,0,90,0.32)"
            >
              <Box position="absolute" w="320px" h="320px" bg="#8b2fd6" opacity={0.4} top="-110px" right="-70px" borderRadius="full" filter="blur(70px)" animation={`${float} 12s ease-in-out infinite`} pointerEvents="none" />
              <Box position="absolute" w="240px" h="240px" bg="#E51F44" opacity={0.18} bottom="-90px" left="-50px" borderRadius="full" filter="blur(70px)" animation={`${float} 12s ease-in-out infinite`} sx={{ animationDelay: "3s" }} pointerEvents="none" />

              <Text position="relative" fontSize="12.5px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" opacity={0.8} mb="12px">
                Zaproszenie do głosowania
              </Text>
              <Heading position="relative" fontSize={{ base: "28px", md: "40px" }} fontWeight="800" lineHeight="1.1" letterSpacing="-0.5px" mb={pageDescription ? "14px" : 0}>
                {pageTitle}
              </Heading>
              {pageDescription && (
                <Text position="relative" fontSize={{ base: "15px", md: "16px" }} opacity={0.88} lineHeight="1.6" maxW="640px">
                  {pageDescription}
                </Text>
              )}

              <HStack position="relative" spacing="8px" mt="22px" wrap="wrap">
                {qCount > 0 && (
                  <HeroChip icon={FiList}>
                    {qCount} {plQuestions(qCount)}
                  </HeroChip>
                )}
                {model.votingPreview && (
                  <HeroChip icon={model.votingPreview.secret ? FiLock : FiEye}>
                    {model.votingPreview.secret ? "Głosowanie tajne" : "Jawne imienne"}
                  </HeroChip>
                )}
                {signup && <HeroChip icon={FiCalendar}>{signup}</HeroChip>}
              </HStack>
            </Box>

            {/* AKCJA — zapis od razu pod hero */}
            <SignUpForVotingForm model={model} />

            {/* Pytania — co zdecydujesz */}
            {model.votingPreview && <VotingPreviewBox preview={model.votingPreview} />}

            {/* Jak to działa */}
            <Box bg="white" borderRadius="20px" boxShadow="0 8px 24px rgba(67,0,152,0.06)" p={{ base: "22px", md: "26px" }}>
              <Text fontSize="13px" fontWeight="700" color="primary" mb="16px">
                Jak to działa
              </Text>
              <VStack align="stretch" spacing="14px">
                {STEPS.map((s, i) => (
                  <HStack key={i} align="flex-start" spacing="13px">
                    <Flex w="30px" h="30px" borderRadius="9px" bg="rgba(67,0,152,0.07)" align="center" justify="center" flexShrink={0}>
                      <Icon as={s.icon} boxSize="15px" color="primary" />
                    </Flex>
                    <Text fontSize="14px" color="gray.700" pt="5px" lineHeight="1.5">
                      {s.text}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            {/* Organizator — drugorzędnie */}
            <Box bg="white" borderRadius="20px" boxShadow="0 8px 24px rgba(67,0,152,0.06)" p={{ base: "18px", md: "22px" }}>
              <Text fontSize="11.5px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="gray.400" mb="14px">
                Organizuje
              </Text>
              <OrganizerData model={model} />
            </Box>

            <LandingPageQR model={model} />

            <LandingPageDescriptions model={model} />
          </VStack>
      </Box>
    </AdaptiveShell>
  );
});

const LandingPageViewWithModel = withModel<LandingPageViewModel>(LandingPageView, LandingPageViewModel);

export default LandingPageViewWithModel;
